import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaUpload } from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import CheckoutRegistrationModal from "../course-checkout/CheckoutRegistrationModal";

interface PaymentSlipUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (paymentId: string) => void;
  paymentDetails: {
    paymentMethod: string;
    transactionId?: string;
    bankName?: string;
    branchName?: string;
    referenceNumber?: string;
    paymentDate: string;
    courseId: number;
    courseTitle: string;
    amount: number; // full total sent to backend (e.g. 156,000)
    displayAmount: number; // amount shown to user (e.g. 26,000 for installment)
    paymentType: string;
    level?: string;
  };
}

const PaymentSlipUploadModal: React.FC<PaymentSlipUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  paymentDetails,
}) => {
  const [paymentSlip, setPaymentSlip] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState<"success" | "error">(
    "success",
  );
  const [uploadedPaymentId, setUploadedPaymentId] = useState<string>("");
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);

  const { isAuthenticated, student } = useAuth();
  const navigate = useNavigate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError("File size must be less than 2MB");
        setPaymentSlip(null);
        return;
      }
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!validTypes.includes(file.type)) {
        setError("Only PDF, JPG, or PNG files are allowed");
        setPaymentSlip(null);
        return;
      }
      setPaymentSlip(file);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!paymentSlip) {
      setError("Please select a file to upload");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();

      const studentId =
        isAuthenticated && student?.studentId ? student.studentId : null;

      const paymentData = {
        purpose: "COURSE",
        studentId: studentId,
        courseId: paymentDetails.courseId,
        paymentType:
          paymentDetails.paymentType === "onetime" ? "full" : "installment",
        amount: paymentDetails.amount, // ✅ always full total for backend
        paymentMethod: paymentDetails.paymentMethod,
        transactionId: paymentDetails.transactionId || "",
        bankName: paymentDetails.bankName || "",
        branchName: paymentDetails.branchName || "",
        referenceNumber: paymentDetails.referenceNumber || "",
        paymentDate: paymentDetails.paymentDate,
      };

      formData.append(
        "payment",
        new Blob([JSON.stringify(paymentData)], { type: "application/json" }),
      );
      formData.append("paymentSlip", paymentSlip);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/payments`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
        },
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Upload failed");

      setUploadedPaymentId(result.paymentId);

      if (isAuthenticated && studentId) {
        try {
          const levelParam = paymentDetails.level
            ? `?level=${encodeURIComponent(paymentDetails.level)}`
            : "";
          const enrollResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/payments/${result.paymentId}/create-enrollment${levelParam}`,
            {
              method: "POST",
              credentials: "include",
            },
          );

          if (!enrollResponse.ok) {
            const error = await enrollResponse.json();
            throw new Error(error.message || "Failed to create enrollment");
          }

          setConfirmationType("success");
          setShowConfirmation(true);
        } catch (enrollError: any) {
          console.error("❌ Failed to create enrollment:", enrollError);
          setConfirmationType("error");
          setError(
            "Payment uploaded but enrollment creation failed. Please contact support.",
          );
        }
      } else {
        setConfirmationType("success");
        setShowConfirmation(true);
      }
    } catch (err: any) {
      console.error("Upload error:", err);
      setConfirmationType("error");
      setError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRegisterClick = () => {
    sessionStorage.setItem("justRegisteredPaymentId", uploadedPaymentId);
    sessionStorage.setItem(
      "justRegisteredCourseTitle",
      paymentDetails.courseTitle,
    );
    setShowConfirmation(false);
    setShowRegistrationModal(true);
  };

  const handleLoginClick = () => {
    sessionStorage.setItem("pendingPaymentId", uploadedPaymentId);
    sessionStorage.setItem(
      "pendingPaymentCourseTitle",
      paymentDetails.courseTitle,
    );
    onClose();
    navigate("/login");
  };

  const handleRegistrationSuccess = () => {
    setShowRegistrationModal(false);
    onClose();
    navigate("/login");
  };

  const handleGoToDashboard = () => {
    navigate("/student/student-dashboard");
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {showConfirmation ? (
            <div className="p-8 text-center">
              <div className="mb-6">
                <FaCheckCircle className="mx-auto text-7xl text-green-500" />
              </div>

              {isAuthenticated ? (
                <>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">
                    Enrollment Request Received
                  </h3>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 text-left">
                    <p className="text-gray-700 leading-relaxed">
                      We've received your enrollment request. Thank you for
                      joining{" "}
                      <strong className="text-[#009fe3]">
                        Guideline2Germany!
                      </strong>
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-3">
                      Our team will review your payment and confirm your
                      enrollment soon.
                    </p>
                    <p className="text-gray-700 leading-relaxed mt-3">
                      Please check your email for updates and next steps.
                    </p>
                    <p className="text-[#009fe3] leading-relaxed mt-3">
                      Your dashboard menus will be activated once G2G reviews
                      and approves your enrollment!
                    </p>
                  </div>

                  <button
                    onClick={handleGoToDashboard}
                    className="w-full py-3 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] font-semibold transition text-lg"
                  >
                    Go to Dashboard
                  </button>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">
                    Upload Successful!
                  </h3>

                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Your payment slip has been uploaded successfully. Please
                    create an account or login to complete your enrollment.
                  </p>

                  <div className="space-y-3">
                    <button
                      onClick={handleRegisterClick}
                      className="w-full py-3 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] font-semibold transition"
                    >
                      Create New Account
                    </button>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">OR</span>
                      </div>
                    </div>
                    <button
                      onClick={handleLoginClick}
                      className="w-full py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold transition"
                    >
                      Login to Existing Account
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  Upload Payment Slip
                </h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Course</p>
                  <p className="font-semibold text-gray-900 mb-3">
                    {paymentDetails.courseTitle}
                  </p>

                  {paymentDetails.level && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600">Level</p>
                      <p className="font-medium text-gray-900">
                        {paymentDetails.level}
                      </p>
                    </div>
                  )}

                  <div className="mb-3">
                    <p className="text-sm text-gray-600">Payment Type</p>
                    <p className="font-medium text-gray-900">
                      {paymentDetails.paymentType === "onetime" ||
                      paymentDetails.paymentType === "full"
                        ? "One-Time Payment"
                        : "Installment"}
                    </p>
                  </div>

                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-600">Amount to Pay Now</p>
                    {/* ✅ Show displayAmount to user (per-installment or full for one-time) */}
                    <p className="text-2xl font-bold text-[#009fe3]">
                      {paymentDetails.displayAmount.toLocaleString()} BDT
                    </p>
                    {/* Show total course fee below if installment */}
                    {paymentDetails.paymentType === "installment" && (
                      <p className="text-xs text-gray-500 mt-1">
                        Total course fee:{" "}
                        {paymentDetails.amount.toLocaleString()} BDT
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Receipt/Screenshot *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-[#009fe3] transition-colors">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      id="payment-slip-upload"
                    />
                    <label
                      htmlFor="payment-slip-upload"
                      className="cursor-pointer block text-center"
                    >
                      {paymentSlip ? (
                        <div className="text-green-600">
                          <FaCheckCircle className="mx-auto text-4xl mb-2" />
                          <p className="font-medium">{paymentSlip.name}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {(paymentSlip.size / 1024).toFixed(2)} KB
                          </p>
                          <p className="text-xs text-blue-600 mt-3">
                            Click to change file
                          </p>
                        </div>
                      ) : (
                        <div className="text-gray-600">
                          <FaUpload className="mx-auto text-4xl mb-2" />
                          <p className="font-medium mb-1">
                            Click to upload payment slip
                          </p>
                          <p className="text-sm text-gray-500">
                            PDF, JPG, or PNG (Max 2MB)
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-3 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={uploading || !paymentSlip}
                    className="flex-1 px-4 py-3 bg-[#009fe3] text-white rounded-lg hover:bg-[#007cb5] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploading ? "Uploading..." : "Upload & Continue"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <CheckoutRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSuccess={handleRegistrationSuccess}
        paymentInfo={{
          paymentId: uploadedPaymentId,
          courseTitle: paymentDetails.courseTitle,
          amount: paymentDetails.displayAmount, // ✅ show per-installment in registration modal too
          paymentType: paymentDetails.paymentType,
          level: paymentDetails.level,
        }}
      />
    </>
  );
};

export default PaymentSlipUploadModal;
